import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { api } from '@/lib/api'
import type {
  ContactTarget,
  ContactTargetListResponse,
  TargetStatus,
  IntroductionPath,
} from '@/lib/types'

export function useContactTargets(status?: TargetStatus) {
  return useQuery({
    queryKey: ['contact-targets', status],
    queryFn: () => {
      const params = status ? `?status=${status}` : ''
      return api<ContactTargetListResponse>(`/api/contact-targets${params}`).then((r) => r.items)
    },
  })
}

export function useContactTarget(id: string | undefined) {
  return useQuery({
    queryKey: ['contact-targets', id],
    queryFn: () => api<ContactTarget>(`/api/contact-targets/${id}`),
    enabled: !!id,
  })
}

export function useContactTargetForContact(contactId: string | undefined) {
  return useQuery({
    queryKey: ['contact-target', contactId],
    queryFn: () => api<ContactTarget | null>(`/api/contacts/${contactId}/target`),
    enabled: !!contactId,
  })
}

export function useCreateContactTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      contact_id: string
      status?: TargetStatus
      goal?: string
      strategy_notes?: string
      target_date?: string
    }) =>
      api<ContactTarget>('/api/contact-targets', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-targets'] })
      qc.invalidateQueries({ queryKey: ['contact-target'] })
      toast.success('Target created')
    },
    onError: () => {
      toast.error('Failed to create target')
    },
  })
}

export function useUpdateContactTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      status?: TargetStatus
      goal?: string | null
      strategy_notes?: string | null
      target_date?: string | null
    }) =>
      api<ContactTarget>(`/api/contact-targets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-targets'] })
      qc.invalidateQueries({ queryKey: ['contact-target'] })
      toast.success('Target updated')
    },
    onError: () => {
      toast.error('Failed to update target')
    },
  })
}

export function useDeleteContactTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/contact-targets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-targets'] })
      qc.invalidateQueries({ queryKey: ['contact-target'] })
      toast.success('Target removed')
    },
    onError: () => {
      toast.error('Failed to remove target')
    },
  })
}

export function useIntroductionPaths(targetId: string | undefined) {
  return useQuery({
    queryKey: ['introduction-paths', targetId],
    queryFn: () =>
      api<{ paths: IntroductionPath[] }>(`/api/contact-targets/${targetId}/paths`).then(
        (r) => r.paths,
      ),
    enabled: !!targetId,
  })
}
